// =============================================================================
// useRealtime - Ponte tra il socket e la cache di React Query
// =============================================================================
// Il server manda segnali di invalidazione, non dati: qui quei segnali
// diventano `invalidateQueries` sulle chiavi corrispondenti. React Query
// rifetcha solo le query montate, quindi una modifica fatta da un collega si
// traduce nelle richieste minime per aggiornare cio' che e' davvero a schermo,
// riusando gli stessi endpoint (e quindi lo stesso fuso, gli stessi permessi,
// gli stessi DTO) della navigazione normale.
//
// Gli eventi personali (inviti, appartenenza) non toccano la cache: cambiano
// dove l'utente si trova. Perdere l'accesso a un'organizzazione mentre la si
// guarda deve riportare al selettore — o all'onboarding, se non resta nessuna
// organizzazione — non lasciare una schermata che comincia a rispondere 403.
// =============================================================================

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useTenantStore } from '../store/tenantStore';
import { useToastStore } from '../store/toastStore';
import { getRealtimeSocket, closeRealtimeSocket } from '../lib/realtime/socket';
import { queryKeysForTopics } from '../lib/realtime/topics';
import { refreshMyMemberships } from '../api/hooks';

interface TenantChangedEvent {
  organizationId: string;
  topics: string[];
}

type UserEventType =
  | 'invite.received'
  | 'invite.revoked'
  | 'membership.added'
  | 'membership.removed'
  | 'tenant.deleted'
  | 'tenant.restored';

interface UserChangedEvent {
  type: UserEventType;
  organizationId?: string;
  tenantName?: string;
}

export function useRealtime(): void {
  const token = useAuthStore((s) => s.token);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const activeTenantId = useTenantStore((s) => s.activeTenantId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  // I riferimenti evitano di ricreare il socket a ogni render: la connessione
  // dipende solo dal token, la sottoscrizione solo dall'organizzazione attiva.
  const tenantRef = useRef(activeTenantId);
  tenantRef.current = activeTenantId;
  const translateRef = useRef(t);
  translateRef.current = t;

  // ---------------------------------------------------------------------------
  // Connessione e listener (dipendono dal token)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isAuthenticated || !token) {
      closeRealtimeSocket();
      return;
    }

    const socket = getRealtimeSocket(token);
    const tr = (key: string, options?: Record<string, unknown>) =>
      translateRef.current(key, options) as string;

    /** Rientro nella room dopo ogni (ri)connessione. */
    const subscribe = () => {
      const organizationId = tenantRef.current;
      if (organizationId) {
        socket.emit('tenant:subscribe', { organizationId });
      }
    };

    // Durante una disconnessione gli eventi non vengono accodati: al rientro
    // non si sa cosa sia cambiato, quindi si riparte da una invalidazione
    // completa. Solo dalla *seconda* connessione in poi: al primo collegamento
    // i dati sono appena stati caricati.
    let hasConnectedOnce = socket.connected;

    const onConnect = () => {
      subscribe();
      if (hasConnectedOnce) {
        void queryClient.invalidateQueries();
      }
      hasConnectedOnce = true;
    };

    const onTenantChanged = (event: TenantChangedEvent) => {
      // Il cambio organizzazione e l'uscita dalla room non sono simultanei:
      // un evento della room precedente puo' ancora arrivare, e non deve
      // invalidare la cache di quella che si sta guardando adesso.
      if (event.organizationId !== tenantRef.current) return;

      for (const queryKey of queryKeysForTopics(event.topics)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    };

    /**
     * L'accesso a un'organizzazione e' stato revocato. Se e' quella aperta,
     * si esce subito: restare significherebbe guardare dati che la prossima
     * richiesta rifiuterebbe con un 403.
     */
    const leaveOrganization = async (organizationId: string | undefined, message: string) => {
      const toast = useToastStore.getState();
      const wasActive = !!organizationId && organizationId === tenantRef.current;

      if (wasActive) {
        useTenantStore.getState().clearTenant();
        queryClient.clear();
        toast.warning(tr('realtime.accessRevoked'), message);
      } else {
        toast.info(message);
      }

      // La destinazione dipende da cosa resta: senza piu' nessuna
      // organizzazione il selettore sarebbe una lista vuota da cui si puo'
      // solo uscire, mentre l'onboarding offre le azioni che hanno senso
      // (crearne una, accettare un invito, ripristinarne una eliminata).
      // Si aspetta il refresh proprio per non decidere sulla lista svuotata
      // da `clearTenant`.
      const memberships = await refreshMyMemberships().catch(() => []);

      if (wasActive) {
        navigate(memberships.length > 0 ? '/select-tenant' : '/onboarding', {
          replace: true,
        });
      }
    };

    const onUserChanged = async (event: UserChangedEvent) => {
      const toast = useToastStore.getState();
      const name = event.tenantName;

      switch (event.type) {
        case 'invite.received':
          void queryClient.invalidateQueries({ queryKey: ['myInvites'] });
          toast.info(
            name
              ? tr('realtime.inviteReceived', { name })
              : tr('realtime.inviteReceivedGeneric'),
          );
          break;

        case 'invite.revoked':
          void queryClient.invalidateQueries({ queryKey: ['myInvites'] });
          break;

        case 'membership.added':
          // Tipicamente arriva quando l'invito e' stato accettato da un'altra
          // scheda o da un altro dispositivo.
          await refreshMyMemberships().catch(() => undefined);
          void queryClient.invalidateQueries({ queryKey: ['myInvites'] });
          if (name) toast.success(tr('realtime.membershipAdded', { name }));
          break;

        case 'membership.removed':
          await leaveOrganization(
            event.organizationId,
            name
              ? tr('realtime.membershipRemoved', { name })
              : tr('realtime.membershipRemovedGeneric'),
          );
          break;

        case 'tenant.deleted':
          await leaveOrganization(
            event.organizationId,
            name
              ? tr('realtime.tenantDeleted', { name })
              : tr('realtime.tenantDeletedGeneric'),
          );
          break;

        case 'tenant.restored':
          await refreshMyMemberships().catch(() => undefined);
          void queryClient.invalidateQueries({ queryKey: ['myDeletedTenants'] });
          if (name) toast.success(tr('realtime.tenantRestored', { name }));
          break;
      }
    };

    // Un token rifiutato dall'handshake (scaduto, revocato da un cambio
    // password) non va ritentato all'infinito: socket.io altrimenti riproverebbe
    // per sempre con la stessa credenziale. Il logout lo gestira' l'interceptor
    // HTTP alla prima chiamata REST.
    const onConnectError = (error: Error & { data?: { code?: string } }) => {
      if (error.data?.code === 'realtime/unauthorized') {
        closeRealtimeSocket();
      }
    };

    socket.on('connect', onConnect);
    socket.on('tenant:changed', onTenantChanged);
    socket.on('user:changed', onUserChanged);
    socket.on('connect_error', onConnectError);

    if (socket.connected) {
      subscribe();
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('tenant:changed', onTenantChanged);
      socket.off('user:changed', onUserChanged);
      socket.off('connect_error', onConnectError);
    };
  }, [token, isAuthenticated, queryClient, navigate]);

  // ---------------------------------------------------------------------------
  // Cambio organizzazione: stesso socket, room diversa
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const socket = getRealtimeSocket(token);
    if (!socket.connected) return;

    if (activeTenantId) {
      socket.emit('tenant:subscribe', { organizationId: activeTenantId });
    } else {
      socket.emit('tenant:unsubscribe');
    }
  }, [activeTenantId, token, isAuthenticated]);
}
